import Vue from 'vue'
import Vuex from 'vuex'
import congressService from '@/services/congresses.js'
import hotelsService from '@/services/hotels.js'
import poisService from '@/services/pois.js'
import cmsService from '@/services/cms.js'
import router from '../router'

import { filterRecommendedHotels, getSixRecommendedHotels, addDistancesToHotels, setCssProperties } from './helpers'

Vue.use(Vuex)

const state = {
  congress: null,
  recommendedHotels: null,
  dataIsReady: false,
  hotels: null,
  pois: null,
  filters: {
    ammenities: [],
    distanceFromLat: 0,
    distanceFromLng: 0,
    distanceInKm: '',
    endPrice: '',
    finishDate: '',
    hotelName: '',
    isBestValue: false,
    isCityCenter: false,
    isRecommended: false,
    isWalkingDistance: false,
    isMapView: false,
    neighborhoods: [],
    roomType: [],
    rooms: '',
    stars: [],
    startDate: '',
    startPrice: ''
  },
  site: null
}

const getters = {
  congress: state => {
    return state.congress
  },
  recommendedHotels: state => {
    return state.recommendedHotels
  },
  dataIsReady: state => {
    return state.dataIsReady
  },
  hotel: state => hotelId => {
    return state.hotels.find(hotel => hotel.hotelId === hotelId)
  },
  pois: state => {
    return state.pois
  },
  congressName: state => {
    return state.congress.name
  },
  cityCenterLat: state => {
    return state.congress.cityCenterLat
  },
  cityCenterLng: state => {
    return state.congress.cityCenterLng
  },
  congressLat: state => {
    return state.congress.lat
  },
  congressLng: state => {
    return state.congress.lng
  },
  includedRates: state => {
    return state.congress.metadata.includedRates
  },
  congressEmail: state => {
    return state.congress.email
  },
  filters: state => {
    return state.filters
  },
  site: state => {
    return state.site
  },
  hotelsByDistance: state => {
    const hotels = state.site.hotelsByDistance
    return (hotels.length && hotels[0] !== null) ? hotels : []
  },
  hotelsByValue: state => {
    const hotels = state.site.hotelsByValue
    return (hotels.length && hotels[0] !== null) ? hotels : []
  },
  hotelsByCenter: state => {
    const hotels = state.site.hotelsByCenter
    return (hotels.length && hotels[0] !== null) ? hotels : []
  },
  hotelsRecommended: state => {
    const hotels = state.site.hotelsRecommended
    return (hotels.length && hotels[0] !== null) ? hotels : []
  },
  generalTerms: state => {
    return state.congress.generalTerms || ''
  }
}

const mutations = {
  ADD_CONGRESS: (state, congress) => {
    state.congress = congress
  },
  ADD_RECOMMENDED_HOTELS: (state, recommendedHotels) => {
    state.recommendedHotels = recommendedHotels
  },
  DATA_IS_READY: (state, boolean) => {
    state.dataIsReady = boolean
  },
  HOTELS: (state, hotels) => {
    state.hotels = hotels
  },
  POIS: (state, pois) => {
    state.pois = pois
  },
  ADD_FILTER: (state, filter) => {
    state.filters[filter.name] = filter.value
  },
  SITE: (state, site) => {
    state.site = site
  }
}

const actions = {
  fetchData: async (context) => {
    context.commit('DATA_IS_READY', false)
    let congress = {}
    return new Promise(async (resolve, reject) => {
      try {
        const site = await cmsService.getSite(process.env.VUE_APP_CMS_SITE_ID);
        context.commit('SITE', site);
        setCssProperties(site);
        const result = await Promise.all([
          congressService.getOneByCode(state.site.VUE_APP_CONGRESS_CODE),
          hotelsService.getAll(state.site.VUE_APP_BUDGET_ID)
        ])
        if (result.length === 0) {
          router.push('/404')
        }
        congress = result[0].data
        const pois = await poisService.getAllByCity(context.state.site.VUE_APP_CONGRESS_CITY)
        congress.hotels = result[1].hotels
        addDistancesToHotels({
          hotels: congress.hotels,
          cityCenterLat: congress.cityCenterLat,
          cityCenterLng: congress.cityCenterLng,
          congressLat: congress.lat,
          congressLng: congress.lng
        })
        const allRecommendedHotels = filterRecommendedHotels(congress.hotels, context.getters.hotelsRecommended)
        const recommendedHotels = getSixRecommendedHotels(allRecommendedHotels)
        context.commit('ADD_CONGRESS', congress)
        context.commit('HOTELS', congress.hotels)
        context.commit('ADD_RECOMMENDED_HOTELS', recommendedHotels)
        context.commit('POIS', pois)
        context.commit('DATA_IS_READY', true)
      } catch (e) {
        throw new Error(e)
      }
    })
  },
  addFilters: (context, filter) => {
    context.commit('ADD_FILTER', filter)
  }
}

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters
})
